import { useState, useEffect, useCallback } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { User, CreatorProfile } from '@/types';
import { unifiedAuthService } from './unified-auth-service';

// Define Permission type locally to avoid import issues
type Permission = string;

export function useUnifiedAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [profile, setProfile] = useState<User | null>(null);
  const [creatorProfile, setCreatorProfile] = useState<CreatorProfile | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const unsubscribeAuth = unifiedAuthService.onAuthStateChange((newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsAuthenticated(!!newSession);
      setIsLoading(unifiedAuthService.isLoading());
    });
    
    const unsubscribeProfile = unifiedAuthService.onProfileChange((newProfile) => {
      setProfile(newProfile);
      setCreatorProfile(unifiedAuthService.getCreatorProfile());
      // Clear stale profile-load errors once a members row resolves
      if (newProfile) setError(null);
    });
    
    const unsubscribeError = unifiedAuthService.onError((nextError) => {
      // null signals that the error was cleared (e.g. sign-out, profile success)
      setError(nextError);
    });
    
    setSession(unifiedAuthService.getSession());
    setUser(unifiedAuthService.getUser());
    setIsAuthenticated(unifiedAuthService.isAuthenticated());
    setProfile(unifiedAuthService.getUserProfile());
    setCreatorProfile(unifiedAuthService.getCreatorProfile());
    setIsLoading(unifiedAuthService.isLoading());
    
    return () => {
      unsubscribeAuth();
      unsubscribeProfile();
      unsubscribeError();
    };
  }, []);
  
  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await unifiedAuthService.signIn(email, password);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.signInWithProvider('google');
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signUp = useCallback(async (
    email: string, 
    password: string, 
    metadata?: Record<string, any>
  ) => {
    setError(null);
    try {
      await unifiedAuthService.signUp(email, password, metadata);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signOut = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.signOut();
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const refreshSession = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.refreshSession();
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const refreshProfile = useCallback(async () => {
    setError(null);
    try {
      const newProfile = await unifiedAuthService.getProfile(true);
      return newProfile;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const updateProfile = useCallback(async (updates: Partial<User>) => {
    setError(null);
    try {
      const updatedProfile = await unifiedAuthService.updateProfile(updates);
      return updatedProfile;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const updatePassword = useCallback(async (newPassword: string) => {
    setError(null);
    try {
      await unifiedAuthService.updatePassword(newPassword);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const resetPassword = useCallback(async (email: string) => {
    setError(null);
    try {
      await unifiedAuthService.resetPassword(email);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const hasPermission = useCallback((permission: Permission): boolean => {
    return unifiedAuthService.hasPermission(permission);
  }, []);
  
  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    return unifiedAuthService.hasAnyPermission(permissions);
  }, []);
  
  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    return unifiedAuthService.hasAllPermissions(permissions);
  }, []);
  
  const isCreator = profile?.role === 'creator' || profile?.role === 'admin';
  const isAdmin = profile?.role === 'admin';
  const userId = user?.id;
  const userEmail = user?.email;
  
  return {
    session,
    user,
    isAuthenticated,
    
    profile,
    creatorProfile,
    
    userId,
    userEmail,
    isCreator,
    isAdmin,
    
    isLoading,
    error,
    
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    refreshSession,
    
    refreshProfile,
    updateProfile,
    updatePassword,
    resetPassword,
    
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}